import { Engine, Actor, Scene, Color, Vector, CircleCollider, Timer, Animation, SpriteSheet, vec } from 'excalibur';
import { derived, Resources } from '../../resources';
import { collision_groups } from '../../scenes/level-one/collision_groups';
import { LevelOne } from '../../scenes/level-one/level-one';
import { range } from '../../util';
import { Bullet } from '../bullet';
import {Enemy} from '../enemies/enemy';
import { TowerSpec } from '../hud/tower_specs';

type TargetingMode = 'closest' | 'farthest' | 'first' | 'last' | 'random';

const targetingModes: TargetingMode[] = ['closest', 'farthest', 'first', 'last', 'random'];

export abstract class Tower extends Actor {
    public ready: boolean
    public x: number
    public y: number

    private towerRange: TowerRange;
    private occupyingCursor = false;

    protected targetingMode: TargetingMode = 'closest';

    private randomLock: Enemy | null = null;

    constructor(protected level: LevelOne, x: number, y:number, private range = 300) {
        super({
            x: x, y: y, radius: 50, color: Color.Green,
            z: 1,
            collisionGroup: collision_groups.towerGroup,
        })
        this.x = x
        this.y = y
        this.ready = true
    }

    abstract getSpec(): TowerSpec;

    onInitialize() {
        this.towerRange = new TowerRange(this.range);
        this.addChild(this.towerRange);

        this.on('pointerenter', () => {
            if (!this.level.hud.cursorOccupied) {
                this.occupyingCursor = true;
                this.level.hud.bottomBar.descriptionBox.setDescription(
                    this.getSpec().name,
                    '\nTargeting Mode:  ' + this.targetingMode + "  (click to change)",
                )
            }
        });

        this.on('pointerleave', (event) => {
            if (event.type === 'move') {
                if (this.occupyingCursor) {
                    this.level.hud.bottomBar.descriptionBox.clearDescription();
                }
            }
        });

        this.on('pointerup', (event) => {
            if (this.occupyingCursor) {
                this.targetingMode = targetingModes[(targetingModes.indexOf(this.targetingMode) + 1) % targetingModes.length];

                this.randomLock = null;

                this.level.hud.bottomBar.descriptionBox.setDescription(
                    this.getSpec().name,
                    '\nTargeting Mode:  ' + this.targetingMode + "  (click to change)",
                )
            }
        });
    }    

    onPostUpdate() {
        this.faceTowardsPreferredEnemy();
    }

    reset() {
        this.ready = true 
    }

    public abstract shoot(results: number[]);

    shootEnemy(target: Enemy, damage: number) {
        let bullet: Bullet = new Bullet(this.x, this.y, damage, target)
        this.scene.add(bullet)
        bullet.follow()
    }

    protected getPreferredEnemy(): Enemy | null {
        switch (this.targetingMode) {
            case 'closest':
                return this.getClosestEnemy();
            case 'farthest':
                return this.getFarthestEnemy();
            case 'first':
                return this.getFirstEnemy();
            case 'last':
                return this.getLastEnemy();
            case 'random':
                return this.getRandomEnemy();
        }
    }

    private getClosestEnemy(): Enemy | null {
        let minDist: number = 1000000
        let closest: Enemy | null = null;
        
        for (let enemy of this.towerRange.inRange) {
            let dist: number = this.pos.distance(enemy.pos)
            if (dist < minDist){
                closest = enemy
                minDist = dist
            }
        }

        return closest;
    }

    private getFarthestEnemy(): Enemy | null {
        let maxDist: number = 0
        let farthest: Enemy | null = null;

        for (let enemy of this.towerRange.inRange) {
            let dist: number = this.pos.distance(enemy.pos)
            if (dist > maxDist){
                farthest = enemy
                maxDist = dist
            }
        }

        return farthest;
    }

    private getFirstEnemy() : Enemy | null {
        let minDist: number = 100000000000000;
        let closest: Enemy | null = null;

        for (let enemy of this.towerRange.inRange) {
            let dist: number = enemy.distanceTraveled;
            if (dist < minDist){
                closest = enemy
                minDist = dist
            }
        }

        return closest;
    }

    private getLastEnemy() : Enemy | null {
        let maxDist: number = 0
        let farthest: Enemy | null = null;

        for (let enemy of this.towerRange.inRange) {
            let dist: number = enemy.distanceTraveled;
            if (dist > maxDist){
                farthest = enemy
                maxDist = dist
            }
        }

        return farthest;
    }

    private getRandomEnemy(): Enemy | null {
        if (this.randomLock != null && this.towerRange.inRange.has(this.randomLock)) {
            return this.randomLock;
        }

        const enemies = Array.from(this.towerRange.inRange);

        if (enemies.length > 0) {
            let random = Math.floor(Math.random() * enemies.length);
            this.randomLock = enemies[random];
            return enemies[random];
        }

        return null;
    }

    private faceTowardsPreferredEnemy() {
        const preferred = this.getPreferredEnemy();
        if (preferred != null) {
            this.rotation = preferred.pos.sub(this.pos).toAngle() - Math.PI / 2;
        }
    }

}

class TowerRange extends Actor {
    public inRange: Set<Enemy> = new Set<Enemy>();

    constructor(radius: number) {
        super({
            radius, color: new Color(0, 0, 0, 0.07),
            z: 1000,
            collisionGroup: collision_groups.towerRangeGroup
        })
    }

    onInitialize() {
        this.on('collisionstart', (event) => {
            if (event.other instanceof Enemy) {
                this.inRange.add(event.other);
            }
        });

        this.on('collisionend', (event) => {
            if (event.other instanceof Enemy) {
                this.inRange.delete(event.other);
            }
        });
    }
}